import React, { forwardRef } from "react";
import { useSelector } from "react-redux";

import { FeatureGate } from "@/components/Common/FeatureGate/FeatureGate";
import { NPSUpdatedCalendar } from "@/components/NPS/NPSUpdatedCalendar";
import { UPDATED_CALENDAR_NPS_FLAG } from "@/constants/featureFlags";
import { useCalendarScroll } from "@/features/calendar/hooks/useCalendarScroll";

import { CalendarDateAxis } from "../DateAxis/CalendarDateAxis";
import { useCalendarDimensions } from "../../hooks/useCalendarDimensions";

import CalendarBackground from "./CalendarBackground";
import { CalendarNonWorkingHours } from "./CalendarNonWorkingHours";
import { CalendarWorkingHours } from "./CalendarWorkingHours";
import "./styles.scss";

export const CalendarContainer = forwardRef(function CalendarContainer(
  { workingHours, daysToDisplay, children, className },
  refs,
) {
  const { calendarBackgroundRef, stickyHeaderRef } = refs;
  const privateFlags = useSelector((state) => state.flags.privateFlags);
  const privateFlagsHaveLoaded = useSelector((state) => state.flags.privateFlagsHaveLoaded);
  const { calendarRect } = useCalendarDimensions(calendarBackgroundRef, stickyHeaderRef);
  const { hasScrolledForFirstTime } = useCalendarScroll(calendarRect, daysToDisplay, workingHours);

  return (
    <div
      className="CalendarContainer__body"
      style={{
        opacity: hasScrolledForFirstTime ? 100 : 0,
      }}
    >
      <FeatureGate
        alternateFlagDisabledBehavior={<></>}
        defaultFlagBehavior={true}
        enabled={privateFlags?.[UPDATED_CALENDAR_NPS_FLAG]}
        flagName={UPDATED_CALENDAR_NPS_FLAG}
        hasLoaded={privateFlagsHaveLoaded}
      >
        <NPSUpdatedCalendar />
      </FeatureGate>

      <div
        className={`CalendarContainer__sticky-header-container ${className}`}
        ref={stickyHeaderRef}
      >
        <CalendarDateAxis
          className={daysToDisplay.length === 1 ? "day" : "week"}
          daysToDisplay={daysToDisplay}
        />
      </div>

      <CalendarBackground
        calendarRect={calendarRect}
        daysToDisplay={daysToDisplay}
        ref={calendarBackgroundRef}
      />

      <CalendarNonWorkingHours calendarRect={calendarRect} numberOfDays={daysToDisplay.length} />

      <CalendarWorkingHours
        calendarRect={calendarRect}
        daysToDisplay={daysToDisplay}
        workingHours={workingHours}
      />
      {children}
    </div>
  );
});
